import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useWorkspace } from "@/hooks/use-workspace";
import { toast } from "sonner";
import { toUserMessage } from "@/lib/supabase-errors";

export interface WorkspaceInvite {
  id: string;
  email: string;
  role: string;
  status: string;
  created_at: string;
  expires_at: string;
}

// workspace_invites is not in auto-generated types yet — cast until types are regenerated
type AnyTable = { data: unknown; error: { message: string } | null };
const db = supabase as unknown as {
  from: (table: string) => {
    select: (cols: string) => {
      eq: (...args: unknown[]) => { eq: (...args: unknown[]) => { order: (...args: unknown[]) => Promise<AnyTable> }; maybeSingle: () => Promise<AnyTable> };
    };
    delete: () => {
      eq: (k: string, v: string) => {
        eq: (k: string, v: string) => { select: (cols: string) => Promise<AnyTable> };
      };
    };
  };
};

export function useWorkspaceInvites() {
  const { currentWorkspace } = useWorkspace();
  const queryClient = useQueryClient();
  const workspaceId = currentWorkspace?.id;

  const invitesQuery = useQuery({
    queryKey: ["workspace-invites", workspaceId],
    queryFn: async (): Promise<WorkspaceInvite[]> => {
      if (!workspaceId) return [];
      const { data, error } = await db
        .from("workspace_invites")
        .select("id, email, role, status, created_at, expires_at")
        .eq("workspace_id", workspaceId)
        .eq("status", "pending")
        .order("created_at", { ascending: false }) as unknown as { data: WorkspaceInvite[] | null; error: { message: string } | null };
      if (error) throw new Error(error.message);
      return data ?? [];
    },
    enabled: !!workspaceId,
  });

  const revokeInvite = useMutation({
    mutationFn: async (inviteId: string) => {
      // Restricting delete to status='pending' ensures we don't silently
      // succeed if the invitee accepted between the list fetch and this click.
      // .select() forces the API to return the deleted rows so we can detect
      // the empty-delete case.
      const { data, error } = await db
        .from("workspace_invites")
        .delete()
        .eq("id", inviteId)
        .eq("status", "pending")
        .select("id") as unknown as { data: { id: string }[] | null; error: { message: string } | null };
      if (error) throw new Error(error.message);
      if (!data || data.length === 0) {
        throw new Error(
          "This invite was already accepted and can no longer be revoked.",
        );
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-invites", workspaceId] });
      toast.success("Invite revoked");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, err.message)),
  });

  const resendInvite = useMutation({
    mutationFn: async ({ email, role }: { email: string; role: "admin" | "member" }) => {
      if (!workspaceId) throw new Error("No workspace selected");
      const { data, error } = await supabase.functions.invoke("invite-member", {
        body: { workspace_id: workspaceId, email, role },
      });
      if (error) {
        const body = await (error as { context?: Response }).context
          ?.json()
          .catch(() => null);
        throw new Error(body?.error ?? error.message);
      }
      if (data?.error) throw new Error(data.error);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["workspace-invites", workspaceId] });
      toast.success("Invite resent");
    },
    onError: (err: Error) => toast.error(toUserMessage(err, err.message)),
  });

  return {
    invites: invitesQuery.data ?? [],
    isLoading: invitesQuery.isLoading,
    revokeInvite,
    resendInvite,
  };
}
